import { endpoints } from "../core-api-endpoints.js";

export class FileRepositoryManager {

    constructor(options = {}) {

        const {
            httpClient,
            config = null,
            resourcePath = endpoints.fileRepository.root,
        } = options;

        if (!httpClient)
            throw new Error('[FileRepositoryManager]: "httpClient" is required.');

        this.http = httpClient;
        this.config = config;
        this.resourcePath = this.normalizePath(resourcePath);

    }

    async getRootFolder() {
        return this.get();
    }

    async getRootFolderSubfolders() {
        return this.get('folders');
    }

    async getRootFolderFiles() {
        return this.get('files');
    }

    async createRootFolder(data) {
        return this.post('folders', data);
    }

    async getFolder(folderUid) {
        return this.get(`folder/${encodeURIComponent(folderUid)}`);
    }

    async updateFolder(folderUid, data) {
        return this.put(`folder/${encodeURIComponent(folderUid)}`, data);
    }

    async getFolderSubfolders(folderUid) {
        return this.get(`folder/${encodeURIComponent(folderUid)}/folders`);
    }

    async createFolder(folderUid, data) {
        return this.post(`folder/${encodeURIComponent(folderUid)}/folders`, data);
    }

    async getFolderFiles(folderUid) {
        return this.get(`folder/${encodeURIComponent(folderUid)}/files`);
    }

    async getFolderPermissionContext(folderUid) {
        return this.get(`folder/${encodeURIComponent(folderUid)}/permissions/context`);
    }

    async getFolderPermissions(folderUid) {
        return this.get(`folder/${encodeURIComponent(folderUid)}/permissions`);
    }

    async createFolderPermission(folderUid, data) {
        return this.post(`folder/${encodeURIComponent(folderUid)}/permissions`, data);
    }

    async updateFolderPermission(folderUid, folderPermissionUid, data) {
        return this.put(`folder/${encodeURIComponent(folderUid)}/permissions/${encodeURIComponent(folderPermissionUid)}`, data);
    }

    async deleteFolderPermission(folderUid, folderPermissionUid) {
        return this.delete(`folder/${encodeURIComponent(folderUid)}/permissions/${encodeURIComponent(folderPermissionUid)}`);
    }

    async detachFolderPermissions(folderUid) {
        return this.post(`folder/${encodeURIComponent(folderUid)}/permissions/detach`, {});
    }

    async inheritFolderPermissions(folderUid) {
        return this.post(`folder/${encodeURIComponent(folderUid)}/permissions/inherit`, {});
    }

    async getFile(fileUid) {
        return this.get(`file/${encodeURIComponent(fileUid)}`);
    }

    async updateFile(fileUid, data) {
        return this.put(`file/${encodeURIComponent(fileUid)}`, data);
    }

    async deleteFile(fileUid) {
        return this.delete(`file/${encodeURIComponent(fileUid)}`);
    }

    async deleteFolder(folderUid) {
        return this.delete(`folder/${encodeURIComponent(folderUid)}`);
    }

    getFileContentUrl(fileUid) {
        return this.createServiceUrl(endpoints.fileRepository.fileContent(encodeURIComponent(fileUid)));
    }

    getFileThumbnailUrl(fileUid) {
        return this.createServiceUrl(endpoints.fileRepository.fileThumbnail(encodeURIComponent(fileUid)));
    }

    async get(path = '', query = null) {

        const response = await this.http.get(this.createPath(path), query, {
            responseType: 'json',
        });
        return response.payload;

    }

    async post(path, data) {

        const response = await this.http.post(this.createPath(path), data, {
            contentType: 'json',
            responseType: 'json',
        });
        return response.payload;

    }

    async put(path, data) {

        const response = await this.http.put(this.createPath(path), data, {
            contentType: 'json',
            responseType: 'json',
        });
        return response.payload;

    }

    async delete(path) {

        const response = await this.http.delete(this.createPath(path), null, {
            responseType: 'json',
        });
        return response.payload;

    }

    createPath(path = '') {

        path = this.normalizePath(path).replace(/^\/+/, '');
        return path ? `${this.resourcePath}/${path}` : this.resourcePath;

    }

    createServiceUrl(servicePath) {

        const config = this.config ?? {};
        const baseUrl = this.normalizePath(config.baseUrl, true);
        const apiBaseUrl = this.normalizePath(config.apiBaseUrl);
        servicePath = this.normalizePath(servicePath);

        return baseUrl + apiBaseUrl + servicePath;

    }

    normalizePath(path, skipOpeningCheck = false) {

        if (path === undefined || path === null)
            return '';

        path = String(path);
        if (path === '')
            return path;

        if (!skipOpeningCheck && path.at(0) !== '/')
            path = '/' + path;
        if (path.at(-1) === '/')
            path = path.slice(0, path.length - 1);

        return path;

    }

}
